import { FC, useMemo } from 'react';

import { BundlesLayout, EBundlesViewModel } from '@appcharge/shared-ui';
import { EBundlesInternalViewModel } from 'constants/enums';
import { usePriceForPreview } from 'views/Offers/hooks/usePriceForPreview';
import { renderingUtil } from 'utils/renderingUtil';
import { normalizeBgColor } from 'views/OfferDesign/OfferDesign.utils';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';

import { OfferUIData, PreviewProduct } from './BundlesForm.types';

interface BundlePreviewProps {
  getOfferUI: { data?: OfferUIData };
  productsListForPreview: PreviewProduct[];
  price: number;
  salePercentage: number;
}

export const BundlePreview: FC<BundlePreviewProps> = ({
  getOfferUI,
  productsListForPreview,
  price,
  salePercentage
}) => {
  const offerUIData = getOfferUI.data;
  const { priceDisplay, priceInUsdCents } = usePriceForPreview({ price });

  const productsList = useMemo(() => {
    if (!productsListForPreview.length) return [];

    const hasSale = (salePercentage ?? 0) > 0;
    if (!hasSale) return productsListForPreview;

    return productsListForPreview.map((product, index) => {
      if (index !== 0) return product;
      return {
        ...product,
        strokedSaleQuantity: String(
          product.quantity ?? product.quantityDisplay ?? ''
        )
      };
    });
  }, [productsListForPreview, salePercentage]);

  return (
    <OfferPreviewShell containerClassName="flex justify-center">
      {({ generalTheme, storeScreen }) => {
        if (!offerUIData) return null;

        const viewModel = generalTheme.bundlesViewModel as EBundlesViewModel;
        const internalModel =
          generalTheme.bundlesInternalViewModel ||
          EBundlesInternalViewModel.MAIN_SUB;

        return (
          <BundlesLayout bundlesViewModel={viewModel} isPreview>
            {renderingUtil.renderBundleByInternalModel(internalModel, {
              index: 0,
              ...(offerUIData.backgroundImage && {
                backgroundSrc: offerUIData.backgroundImage
              }),
              ...(offerUIData.bundle?.backgroundColor && {
                backgroundColor: normalizeBgColor(
                  offerUIData.bundle.backgroundColor
                )
              }),
              bundleBorderColor: storeScreen.bundleBorderColor,
              productList: productsList,
              bundleButtonColor: storeScreen.buttonColor,
              bundleButtonTextColor: storeScreen.buttonTextColor,
              fontFamily: generalTheme.font,
              prices: { priceDisplay, priceInUsdCents },
              inMobileMock: true,
              gameName: internalModel
            })}
          </BundlesLayout>
        );
      }}
    </OfferPreviewShell>
  );
};
